/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import PhoneField from '@components/atoms/ui-kit/phone-input.tsx';
import { isPhoneValidForForm } from '@components/atoms/ui-kit/phone-input.tsx';
import { useNavigate } from 'react-router-dom';
import { Send, Link as LinkIcon } from 'lucide-react';

import { usePageHeader } from '@utils/hooks/use-page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetResellerAllUsers } from '@utils/api/resellers/user-get-reseller-all-users.tsx';
import { useCreateResellerCustomer } from '@utils/api/resellers/use-create-reseller-customer.tsx';
import { useResendActivationLink } from '@utils/api/resellers/use-resend-activation-link-customer.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';

import { PATHS } from '../../router/paths.ts';
import { formatDate } from '@utils/date.ts';
import type { BaseError } from '@utils/types/shared/base.ts';
import type { ResellerUser } from '@utils/types/resellers/reseller-all-users.ts';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';

type Row = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  registered_at: string;
  customer_id: number;
  asked_for_reset: boolean;
};

type ValidationErr = { loc?: string[]; msg?: string };

type CreateFormValues = {
  name: string;
  email: string;
  phone: string;
};

type ResellerListEnvelope = {
  result?: ResellerUser[] | { result?: ResellerUser[] };
  total_results?: number;
};

function parseError(err: unknown, fallback: string): string {
  const data = (err as AxiosError<BaseError>)?.response?.data;
  if (Array.isArray(data?.detail)) {
    return (data.detail as ValidationErr[])
      .map(e => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
      .join('\n');
  }
  if (data?.message) return data.message;
  if (data?.error_description) return data.error_description;
  return fallback;
}

export default function ResellerAllUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: resellers, isLoading, isFetching } = useGetResellerAllUsers();
  const createCustomer = useCreateResellerCustomer();
  const resendActivationLink = useResendActivationLink();
  const { data: organization } = useOrganization();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState<Row | null>(null);
  const [resendLinkModal, setRsendLinkModal] = useState(false);
  const [isResendingLink, setIsResendingLink] = useState(false);

  /* ------------ Form ------------- */
  const schema = yup.object({
    name: yup.string().required(t('name-is-required')),
    email: yup.string().email().required(t('email-required')),
    phone: yup
      .string()
      .required(t('phone-required'))
      .test(
        'is-valid-phone',
        t('enter-a-valid-international-phone-number'),
        isPhoneValidForForm
      )
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue
  } = useForm<CreateFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any
  });

  const yesNoBadge = (val: boolean) => (
    <Badge color={val ? 'green' : 'zinc'}>{val ? t('yes') : t('no')}</Badge>
  );

  /* ------------ Table ------------- */
  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('id'),
        maxWidth: 90,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'id',
        serverOperator: 'eq',
        enableRowCopyOnClick: true
      },
      {
        id: 'first_name',
        accessorKey: 'first_name',
        header: t('first-name'),
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'first_name',
        serverOperator: 'ilike',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'last_name',
        accessorKey: 'last_name',
        header: t('last-name'),
        minWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'last_name',
        serverOperator: 'ilike',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: t('email'),
        minWidth: 220,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'email',
        serverOperator: 'ilike',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'is_active',
        accessorKey: 'is_active',
        header: t('is-active'),
        maxWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => yesNoBadge(Boolean(getValue()))
      },
      {
        id: 'registered_at',
        accessorKey: 'registered_at',
        header: t('registered'),
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'customer_id',
        accessorKey: 'customer_id',
        header: t('id'),
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        enableRowCopyOnClick: true
      },
      {
        id: 'asked_for_reset',
        accessorKey: 'asked_for_reset',
        header: t('asked-for-reset'),
        maxWidth: 150,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: 'true' },
          { label: t('no'), value: 'false' }
        ],
        cell: ({ getValue }) => yesNoBadge(Boolean(getValue()))
      }
    ],
    [t]
  );

  const rows: Row[] = useMemo(() => {
    const envelope = resellers as ResellerListEnvelope | undefined;
    const r = envelope?.result;
    const list: ResellerUser[] = Array.isArray(r)
      ? r
      : Array.isArray(r?.result)
        ? r.result
        : [];

    return list.map((r: ResellerUser) => ({
      id: r.id,
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
      is_active: r.is_active,
      registered_at: formatDate(r.registered_at),
      customer_id: r.customer_id,
      asked_for_reset: r.asked_for_reset
    }));
  }, [resellers]);

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'resend_link',
        label: t('send-reactivation-link'),
        icon: <Send className="h-3.5 w-3.5" />,
        run: row => {
          setSelectedUser(row);
          setRsendLinkModal(true);
        }
      },
      {
        id: 'go_customer',
        label: t('go-to-customer'),
        icon: <LinkIcon className="h-3.5 w-3.5" />,
        run: row => {
          const customerId = String(row.customer_id);
          const orgId = String(organization?.id ?? '');
          const targetUrl =
            customerId === orgId
              ? PATHS(customerId).USERS
              : PATHS(customerId).RESELLER_CUSTOMER + '?tab=3';
          navigate(targetUrl);
        }
      }
    ],
    [navigate, organization?.id, t]
  );

  /* ------------ Resend activation ------------ */
  const handleResendActivation = async () => {
    if (!selectedUser) return;
    setIsResendingLink(true);
    await new Promise(r => setTimeout(r, 300));
    resendActivationLink.mutate(
      {
        customerId: Number(selectedUser.customer_id),
        userId: Number(selectedUser.id)
      },
      {
        onSuccess: () => {
          toast.success(t('activation-link-resent-successfully'));
        },
        onError: (err: unknown) => {
          toast.error(parseError(err, t('failed-to-resent-activation-link')));
        },
        onSettled: () => {
          setRsendLinkModal(false);
          setIsResendingLink(false);
        }
      }
    );
  };

  /* ------------ Create customer ------------ */
  const handleCreate = async (values: CreateFormValues) => {
    const { name, email, phone } = values;
    setIsSubmitting(true);
    try {
      await createCustomer.mutateAsync({
        name,
        email,
        phone,
        is_active: true
      });
      toast.success(t('customer-created-successfully'));
      reset();
      setIsModalOpen(false);
    } catch (err: unknown) {
      toast.error(
        parseError(err, t('error-creating-customer-please-try-again-later'))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showEmptyState =
    !isLoading && !isFetching && rows.length === 0 && !!resellers;

  usePageHeader({
    title: t('reseller-internal-users'),
    description: t('a-list-of-all-reseller-users-including-their-details')
  });

  /* ------------ Render ------------ */
  return (
    <div className="bg-app min-h-screen">
      {/* Create new reseller customer */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="small"
        title={t('create-new-reseller-customer')}
        caption={t(
          'fill-in-the-details-below-to-create-a-new-reseller-customer'
        )}
        footerActions={[
          <Button
            key="cancel"
            onClick={() => setIsModalOpen(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            key="create"
            onClick={handleSubmit(handleCreate)}
            label={isSubmitting ? t('creating') : t('create')}
            size="small"
            variant="primary"
            disabled={isSubmitting}
          />
        ]}
      >
        <form>
          <div className="grid gap-4 w-full px-2">
            <Input
              id="name"
              label={t('name')}
              {...register('name')}
              error={errors.name?.message}
            />
            <Input
              id="email"
              type="email"
              label={t('email')}
              {...register('email')}
              error={errors.email?.message}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneField
                  id="phone"
                  label={t('phone')}
                  defaultCountry="NL"
                  value={field.value}
                  onChange={value => setValue('phone', value)}
                  error={errors.phone?.message}
                />
              )}
            />
          </div>
        </form>
      </Modal>

      <div className="relative">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          totalItems={resellers?.total_results ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !resellers}
          getRowId={row => String(row.id)}
        />

        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-reseller-users-yet')}
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Resend activation */}
      <ConfirmationModal
        isOpen={resendLinkModal}
        onClose={() => setRsendLinkModal(false)}
        onConfirm={handleResendActivation}
        size="small"
        title={t('resend-activation-link')}
        caption={t(
          'by-clicking-confirm-you-will-resend-activation-link-to-the-users-email'
        )}
        confirmLabel={isResendingLink ? t('resending') : t('resend')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResendingLink}
        destructive={false}
        autoFocusConfirm
      />
    </div>
  );
}
